// AI-powered TTS via backend /api/tts, falling back to browser SpeechSynthesis.
// Audio responses are cached in-memory by (text+voice) to avoid duplicate network calls.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const blobCache = new Map(); // key -> objectURL
const MAX_CACHE = 50;
let currentAudio = null;

function key(text, voice) {
  return `${voice}::${text}`;
}

async function fetchAudioBlob(text, voice) {
  const k = key(text, voice);
  if (blobCache.has(k)) return blobCache.get(k);
  const res = await fetch(`${API}/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, voice, model: "tts-1", speed: 0.95 }),
  });
  if (!res.ok) throw new Error(`TTS failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  if (blobCache.size >= MAX_CACHE) {
    const firstKey = blobCache.keys().next().value;
    const firstUrl = blobCache.get(firstKey);
    blobCache.delete(firstKey);
    try { URL.revokeObjectURL(firstUrl); } catch (e) {
      // Some browsers can throw on revoke of an already-freed URL; safe to ignore.
      if (process.env.NODE_ENV !== "production") console.debug("[speech] revokeObjectURL ignored:", e);
    }
  }
  blobCache.set(k, url);
  return url;
}

// Read-aloud speed from the Accessibility panel (a11y "readAloudRate", 0.6–1.4). Was stored but never applied before Oct 2026.
export function userRate() {
  try { const v = JSON.parse(localStorage.getItem("mwq_attention_v1") || "{}").readAloudRate; return Number.isFinite(v) && v > 0 ? v : 1; } catch { return 1; }
}
let paused = false;
export function isPaused() { return paused; }
/** Pause/resume whichever engine is speaking (AI audio element or browser synthesis). */
export function pauseSpeaking() {
  paused = true;
  try { if (currentAudio && !currentAudio.paused) currentAudio.pause(); } catch { /* ignore */ }
  try { if (window.speechSynthesis?.speaking) window.speechSynthesis.pause(); } catch { /* ignore */ }
}
export function resumeSpeaking() {
  paused = false;
  try { if (currentAudio && currentAudio.paused && currentAudio.currentTime > 0 && !currentAudio.ended) currentAudio.play().catch(() => {}); } catch { /* ignore */ }
  try { if (window.speechSynthesis?.paused) window.speechSynthesis.resume(); } catch { /* ignore */ }
}

function speakBrowser(text, { rate = 0.95 * userRate(), pitch = 1.15 } = {}) {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const v =
      voices.find((vv) => /female|samantha|karen|tessa|google us english/i.test(vv.name) && vv.lang.startsWith("en")) ||
      voices.find((vv) => vv.lang.startsWith("en"));
    if (v) u.voice = v;
    u.rate = rate;
    u.pitch = pitch;
    u.volume = 1;
    window.speechSynthesis.speak(u);
  } catch (e) {
    // Older browsers / restrictive contexts (e.g. iframes) may not allow synthesis.
    if (process.env.NODE_ENV !== "production") console.debug("[speech] browser TTS unavailable:", e);
  }
}

export function stopSpeaking() {
  paused = false;
  if (currentAudio) {
    try {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    } catch (e) {
      // Audio element already released; safe to ignore.
      if (process.env.NODE_ENV !== "production") console.debug("[speech] stop ignored:", e);
    }
  }
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

// voice options: kid-friendly defaults — nova (energetic) and shimmer (bright)
export async function speak(text, { voice = "nova" } = {}) {
  if (!text) return;
  stopSpeaking();
  try {
    const url = await fetchAudioBlob(text, voice);
    const audio = new Audio(url);
    audio.playbackRate = userRate();
    currentAudio = audio;
    audio.play().catch(() => {
      // Autoplay can be blocked on first load before user gesture — silently degrade
    });
  } catch (e) {
    // Network/TTS failure — fall back to free browser speech.
    if (process.env.NODE_ENV !== "production") console.debug("[speech] AI TTS failed, using browser:", e);
    speakBrowser(text);
  }
}

// Preload helper for known phrases (warms cache)
export async function preload(text, voice = "nova") {
  try {
    await fetchAudioBlob(text, voice);
  } catch (e) {
    // Preload is best-effort — a failure here just means we'll fetch on demand later.
    if (process.env.NODE_ENV !== "production") console.debug("[speech] preload skipped:", e);
  }
}
